import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import jsPDF from "jspdf";
import { patchJsPdfText } from "../lib/pdfTextSanitize";
import { Checkbox } from "../components/ui/checkbox";
import {
  FileText,
  Loader2,
  Download,
  Eye,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Mail,
  Send,
  User,
  Building2,
  CheckSquare,
  Square,
  Trash2,
  X,
  Search,
  RotateCw,
  History,
  Printer,
  Ticket,
  Save,
  Sparkles,
  FileUp,
  MessageCircleQuestion,
  XCircle,
  Flame,
  Car
} from "lucide-react";

// Etiqueta identificadora del informe según el ramo de la comparación.
const ramoInformeLabel = (ramo) => {
  const r = (ramo || "").toLowerCase();
  if (!r) return null;
  if (r.includes("incendio") || r.includes("aliada")) return "Informe de Incendio";
  if (r.includes("vehic") || r.includes("auto")) return "Informe Vehículos";
  return `Informe ${ramo}`;
};

const ramoBadgeClass = (ramo) => {
  const r = (ramo || "").toLowerCase();
  if (r.includes("incendio") || r.includes("aliada"))
    return "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700";
  return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700";
};
import { Badge } from "../components/ui/badge";

const TRAZA_ACCION_META = {
  inicio: { label: "Inicio", color: "bg-blue-100 text-blue-700", Icon: Clock },
  documentos_cargados: { label: "Documentos", color: "bg-indigo-100 text-indigo-700", Icon: FileUp },
  comparacion_procesada: { label: "IA procesó", color: "bg-purple-100 text-purple-700", Icon: Sparkles },
  informe_guardado: { label: "Informe guardado", color: "bg-green-100 text-green-700", Icon: Save },
  pregunta_ticket: { label: "Pregunta ticket", color: "bg-amber-100 text-amber-700", Icon: MessageCircleQuestion },
  ticket_aceptado: { label: "Aceptó", color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  ticket_rechazado: { label: "Rechazó", color: "bg-slate-100 text-slate-600", Icon: XCircle },
  ticket_creado: { label: "Ticket creado", color: "bg-amber-100 text-amber-800", Icon: Ticket },
};

const fmtTrazaFecha = (ts) => {
  if (!ts) return { fecha: "-", hora: "-" };
  try {
    const d = new Date(ts);
    return {
      fecha: d.toLocaleDateString("es-DO", { year: "numeric", month: "2-digit", day: "2-digit" }),
      hora: d.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    };
  } catch {
    return { fecha: ts, hora: "-" };
  }
};

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient_email: "",
    recipient_name: "",
    subject: "",
    message: ""
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Multi-selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());
  const canSeeTraza = isAdmin || (user?.role || "").toLowerCase().includes("supervisor") || user?.is_admin;

  // Trazabilidad modal state
  const [trazaComparison, setTrazaComparison] = useState(null);
  const [trazaLoading, setTrazaLoading] = useState(false);
  const [trazaReport, setTrazaReport] = useState(null);

  const openTrazabilidad = async (report) => {
    if (!report?.comparison_id) {
      toast.error("Este informe no tiene una comparación asociada");
      return;
    }
    setTrazaReport(report);
    setTrazaLoading(true);
    setTrazaComparison(null);
    try {
      const res = await axios.get(`${API}/comparisons/${report.comparison_id}`, getAuthHeaders());
      if (!res.data?.puede_ver_trazabilidad) {
        toast.error("No tiene permiso para ver la trazabilidad de este informe");
        setTrazaReport(null);
        return;
      }
      setTrazaComparison(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al cargar la trazabilidad");
      setTrazaReport(null);
    } finally {
      setTrazaLoading(false);
    }
  };

  const printTrazabilidadPDF = async (comparisonId) => {
    if (!comparisonId) return;
    try {
      const res = await axios.get(
        `${API}/comparisons/${comparisonId}/trazabilidad/pdf`,
        { ...getAuthHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trazabilidad_comparacion_${comparisonId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Trazabilidad descargada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No tiene permiso para imprimir la trazabilidad");
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/reports`, getAuthHeaders());
      setReports(res.data);
    } catch (err) {
      toast.error("Error al cargar los informes");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter reports by search term
  const filteredReports = searchTerm.trim()
    ? reports.filter(r => {
        const term = searchTerm.toLowerCase();
        const title = (r.content?.title || "").toLowerCase();
        const cliente = (r.content?.cliente_nombre || "").toLowerCase();
        const doc1 = (r.content?.documents?.document1 || "").toLowerCase();
        const doc2 = (r.content?.documents?.document2 || "").toLowerCase();
        const creator = (r.content?.created_by_name || r.content?.prepared_by || "").toLowerCase();
        return title.includes(term) || cliente.includes(term) || doc1.includes(term) || doc2.includes(term) || creator.includes(term);
      })
    : reports;

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems(new Set());
  };

  const toggleItemSelection = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    const allIds = new Set(reports.map(r => r.id));
    setSelectedItems(allIds);
  };

  const deselectAllItems = () => {
    setSelectedItems(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteSelected = async () => {
    setDeletingMultiple(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedItems) {
      try {
        await axios.delete(`${API}/reports/${id}`, getAuthHeaders());
        successCount++;
      } catch (error) {
        console.error(`Error deleting report ${id}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} informe(s) eliminado(s) exitosamente`);
      fetchReports();
    }
    if (errorCount > 0) {
      toast.error(`No se pudieron eliminar ${errorCount} informe(s)`);
    }

    setSelectedItems(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);
    setShowDeleteConfirmDialog(false);
  };

  const openEmailDialog = (report) => {
    setSelectedReport(report);
    setEmailData({
      recipient_email: "",
      recipient_name: "",
      subject: `Informe de Comparación - ${report.content?.documents?.document1 || "Póliza"}`,
      message: ""
    });
    setShowEmailDialog(true);
  };

  const sendEmail = async () => {
    if (!emailData.recipient_email) {
      toast.error("El email del destinatario es requerido");
      return;
    }

    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/send-report`, {
        report_id: selectedReport.id,
        recipient_email: emailData.recipient_email,
        recipient_name: emailData.recipient_name || null,
        subject: emailData.subject || null,
        message: emailData.message || null
      }, getAuthHeaders());
      
      toast.success(`Email enviado exitosamente a ${emailData.recipient_email}`);
      setShowEmailDialog(false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Error al enviar el email";
      toast.error(errorMsg);
    } finally {
      setSendingEmail(false);
    }
  };

  // Helper function to load image as base64
  const loadImageAsBase64 = async (url) => {
    // If it's already a data URL (base64), use it directly
    if (url.startsWith('data:')) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 800; // Increased for better quality
          const scale = Math.min(1, maxWidth / img.naturalWidth);
          canvas.width = img.naturalWidth * scale;
          canvas.height = img.naturalHeight * scale;
          
          const ctx = canvas.getContext('2d');
          // Keep transparency by using PNG format
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          // Use PNG to preserve transparency (no black background)
          const dataUrl = canvas.toDataURL('image/png');
          resolve({ dataUrl, width: canvas.width, height: canvas.height });
        };
        img.onerror = () => reject(new Error('Base64 image decode failed'));
        img.src = url;
      });
    }

    // For external URLs, try multiple methods
    try {
      // Method 1: Try using fetch with no-cors (for same-origin or CORS-enabled URLs)
      const response = await fetch(url, { 
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxWidth = 800;
            const scale = Math.min(1, maxWidth / img.naturalWidth);
            canvas.width = img.naturalWidth * scale;
            canvas.height = img.naturalHeight * scale;
            
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            const dataUrl = canvas.toDataURL('image/png');
            resolve({ dataUrl, width: canvas.width, height: canvas.height });
          };
          img.onerror = () => reject(new Error('Image decode failed'));
          img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      console.log("Fetch method failed:", fetchError.message);
      
      // Method 2: Fallback - try direct Image load with crossOrigin
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const maxWidth = 800;
            const scale = Math.min(1, maxWidth / img.naturalWidth);
            canvas.width = img.naturalWidth * scale;
            canvas.height = img.naturalHeight * scale;
            
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            const dataUrl = canvas.toDataURL('image/png');
            resolve({ dataUrl, width: canvas.width, height: canvas.height });
          } catch (err) {
            reject(err);
          }
        };
        
        img.onerror = () => reject(new Error('Direct load failed'));
        img.src = url;
      });
    }
  };

  const downloadReportPDF = async (report) => {
    // If the report has a stored PDF (generated from comparison page), download it directly
    if (report.content?.pdf_data) {
      try {
        // Fetch the full report with pdf_data from backend
        const res = await axios.get(`${API}/reports/${report.id}/pdf`, getAuthHeaders());
        const pdfBase64 = res.data.pdf_data;
        if (pdfBase64) {
          const byteCharacters = atob(pdfBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `analisis-comparativo-${report.id.slice(0, 8)}.pdf`;
          a.click();
          window.URL.revokeObjectURL(url);
          return;
        }
      } catch (err) {
        console.error("Error downloading stored PDF, falling back to generation:", err);
      }
    }
    
    const doc = patchJsPdfText(new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" }));
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const PAGE_MARGIN_BOTTOM = 30; // Extra space for footer (Preparado por + copyright + page num)
    const PAGE_MARGIN_TOP = 15;
    let yPos = 20;
    
    const content = report.content;
    let logoLoaded = false;
    
    // Check if using new detailed format
    const isDetailedFormat = content.analysis_format === "detailed_report";
    
    const getRemainingSpace = () => pageHeight - PAGE_MARGIN_BOTTOM - yPos;
    
    const addNewPage = () => {
      doc.addPage();
      yPos = PAGE_MARGIN_TOP;
    };
    
    const checkNewPage = (neededSpace = 30) => {
      if (yPos + neededSpace > pageHeight - PAGE_MARGIN_BOTTOM) {
        addNewPage();
        return true;
      }
      return false;
    };

    const addSectionTitle = (title, minContentSpace = 40) => {
      const titleBlockHeight = 20 + minContentSpace;
      if (getRemainingSpace() < titleBlockHeight) {
        addNewPage();
      }
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 58, 95);
      doc.text(title, 15, yPos);
      yPos += 2;
      doc.setDrawColor(224, 224, 224);
      doc.line(15, yPos, pageWidth - 15, yPos);
      yPos += 8;
      doc.setTextColor(0, 0, 0);
    };

    const addText = (text, indent = 15, fontSize = 9) => {
      doc.setFontSize(fontSize);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(text, pageWidth - indent - 15);
      lines.forEach(line => {
        checkNewPage(6);
        doc.text(line, indent, yPos);
        yPos += 5;
      });
    };
    
    // Add Logo if available
    if (content.logo_url) {
      try {
        let imageData = null;
        
        try {
          imageData = await loadImageAsBase64(content.logo_url);
          logoLoaded = true;
        } catch (directError) {
          console.log("Direct image load failed, trying proxy...", directError);
          
          try {
            const proxyUrl = `${API}/proxy/image?url=${encodeURIComponent(content.logo_url)}`;
            const response = await axios.get(proxyUrl, getAuthHeaders());
            
            if (response.data && response.data.data) {
              const dataUrl = response.data.data;
              
              const img = new Image();
              await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = dataUrl;
              });
              
              const canvas = document.createElement('canvas');
              const maxWidth = 800;
              const scale = Math.min(1, maxWidth / img.naturalWidth);
              canvas.width = img.naturalWidth * scale;
              canvas.height = img.naturalHeight * scale;
              
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              imageData = {
                dataUrl: canvas.toDataURL('image/png'),
                width: canvas.width,
                height: canvas.height
              };
              logoLoaded = true;
            }
          } catch (proxyError) {
            console.error("Proxy image load also failed:", proxyError);
          }
        }
        
        if (imageData) {
          const maxHeight = 62.5;
          const ratio = imageData.width / imageData.height;
          const logoHeight = maxHeight;
          const logoWidth = Math.min(logoHeight * ratio, pageWidth - 40);
          
          const logoX = (pageWidth - logoWidth) / 2;
          doc.addImage(imageData.dataUrl, 'PNG', logoX, yPos, logoWidth, logoHeight);
          yPos += logoHeight + 10;
        }
      } catch (err) {
        console.error("Error loading logo for PDF:", err);
      }
      
      if (!logoLoaded) {
        toast.warning("El logo no se pudo incluir en el PDF.", { duration: 6000 });
      }
    }
    
    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 58, 95);
    doc.text(content.title || "ANÁLISIS COMPARATIVO Y RECOMENDACIÓN DE PÓLIZA", pageWidth / 2, yPos, { align: "center" });
    yPos += 15;
    doc.setTextColor(0, 0, 0);
    
    // Date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Fecha: ${content.date || new Date().toLocaleDateString('es-ES')}`, pageWidth / 2, yPos, { align: "center" });
    yPos += 15;

    // ============ NEW DETAILED FORMAT ============
    if (isDetailedFormat) {
      // Datos del Asegurado
      if (content.datos_asegurado) {
        addSectionTitle("DATOS DEL ASEGURADO");
        const da = content.datos_asegurado;
        doc.setFontSize(9);
        const aseguradoData = [
          ["Nombre / Razón Social", da.nombre_razon_social || "No especificado"],
          ["Identificación (RNC)", da.identificacion_rnc || "No especificado"],
          ["Dirección", da.direccion || "No especificado"],
          ["Teléfono", da.telefono || "No especificado"],
          ["Correo Electrónico", da.correo_electronico || "No especificado"],
        ];
        if (da.actividad_economica) aseguradoData.push(["Actividad Económica", da.actividad_economica]);
        
        aseguradoData.forEach(([label, value]) => {
          checkNewPage(8);
          doc.setFont("helvetica", "bold");
          doc.text(`${label}:`, 15, yPos);
          doc.setFont("helvetica", "normal");
          doc.text(value, 60, yPos);
          yPos += 6;
        });
        yPos += 5;
      }

      // Datos Generales de las Pólizas
      if (content.datos_generales_polizas?.length > 0) {
        addSectionTitle("DATOS GENERALES DE LAS PÓLIZAS");
        
        const polA = content.datos_generales_polizas[0] || {};
        const polB = content.datos_generales_polizas[1] || {};
        
        const dgRows = [
          ["Tipo de Póliza", polA.tipo_poliza || "N/A", polB.tipo_poliza || "N/A"],
          ["Producto / Plan", polA.producto_plan || "N/A", polB.producto_plan || "N/A"],
          ["Aseguradora", polA.aseguradora || "N/A", polB.aseguradora || "N/A"],
          ["Vigencia", `${polA.vigencia_inicio || "N/A"} - ${polA.vigencia_fin || "N/A"}`, `${polB.vigencia_inicio || "N/A"} - ${polB.vigencia_fin || "N/A"}`],
        ];
        if (polA.numero_poliza || polB.numero_poliza) dgRows.splice(0, 0, ["No. Póliza", polA.numero_poliza || "N/A", polB.numero_poliza || "N/A"]);
        if (polA.referencia || polB.referencia) dgRows.push(["Referencia", polA.referencia || "N/A", polB.referencia || "N/A"]);
        if (polA.ramo || polB.ramo) dgRows.push(["Ramo", polA.ramo || "N/A", polB.ramo || "N/A"]);
        if (polA.moneda || polB.moneda) dgRows.push(["Moneda", polA.moneda || "N/A", polB.moneda || "N/A"]);
        if (polA.forma_pago || polB.forma_pago) dgRows.push(["Forma de Pago", polA.forma_pago || "N/A", polB.forma_pago || "N/A"]);
        if (polA.ncf || polB.ncf) dgRows.push(["NCF", polA.ncf || "N/A", polB.ncf || "N/A"]);
        if (polA.tipo_movimiento || polB.tipo_movimiento) dgRows.push(["Tipo Movimiento", polA.tipo_movimiento || "N/A", polB.tipo_movimiento || "N/A"]);
        
        const dgColWidths = [40, 60, 60];
        const dgTableWidth = dgColWidths.reduce((a, b) => a + b, 0);
        const dgStartX = 15;
        
        const drawDgHeader = () => {
          doc.setFontSize(8);
          doc.setFillColor(240, 240, 240);
          doc.rect(dgStartX, yPos - 4, dgTableWidth, 8, "F");
          doc.setFont("helvetica", "bold");
          let xPos = dgStartX;
          ["ÍTEM", "PÓLIZA ACTUAL", "RENOVACIÓN"].forEach((h, i) => {
            doc.text(h, xPos + 2, yPos);
            xPos += dgColWidths[i];
          });
          yPos += 8;
        };
        
        drawDgHeader();
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        dgRows.forEach(row => {
          if (yPos + 8 > pageHeight - PAGE_MARGIN_BOTTOM) {
            addNewPage();
            drawDgHeader();
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
          }
          let xPos = dgStartX;
          row.forEach((cell, i) => {
            const lines = doc.splitTextToSize(cell, dgColWidths[i] - 4);
            doc.text(lines[0], xPos + 2, yPos);
            xPos += dgColWidths[i];
          });
          yPos += 6;
        });
        yPos += 5;
      }

      // Tabla Comparativa Detallada
      if (content.tabla_comparativa?.length > 0) {
        addSectionTitle("TABLA COMPARATIVA DETALLADA", 50);
        
        const colWidths = [38, 48, 48, 46];
        const tableWidth = colWidths.reduce((a, b) => a + b, 0);
        const startX = 15;
        const HEADER_HEIGHT = 10;
        
        // Reusable header drawing function
        const drawTableHeader = () => {
          doc.setFillColor(100, 116, 139);
          doc.rect(startX, yPos - 4, colWidths[0], HEADER_HEIGHT, "F");
          doc.setFillColor(37, 99, 235);
          doc.rect(startX + colWidths[0], yPos - 4, colWidths[1], HEADER_HEIGHT, "F");
          doc.setFillColor(22, 163, 74);
          doc.rect(startX + colWidths[0] + colWidths[1], yPos - 4, colWidths[2], HEADER_HEIGHT, "F");
          doc.setFillColor(217, 119, 6);
          doc.rect(startX + colWidths[0] + colWidths[1] + colWidths[2], yPos - 4, colWidths[3], HEADER_HEIGHT, "F");
          
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(255, 255, 255);
          doc.text("ÍTEM EVALUADO", startX + 2, yPos + 2);
          doc.text("PÓLIZA ACTUAL", startX + colWidths[0] + 2, yPos + 2);
          doc.text("RENOVACIÓN", startX + colWidths[0] + colWidths[1] + 2, yPos + 2);
          doc.text("OBSERVACIONES", startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos + 2);
          
          doc.setTextColor(0, 0, 0);
          yPos += HEADER_HEIGHT;
        };
        
        // Draw initial header
        drawTableHeader();
        
        // Table rows
        doc.setFontSize(6);
        content.tabla_comparativa.forEach((row, idx) => {
          const itemLines = doc.splitTextToSize(row.item || "", colWidths[0] - 4);
          const polALines = doc.splitTextToSize(row.poliza_a || "N/A", colWidths[1] - 4);
          const polBLines = doc.splitTextToSize(row.poliza_b || "N/A", colWidths[2] - 4);
          const obsLines = doc.splitTextToSize(row.observacion || "-", colWidths[3] - 4);
          const maxLines = Math.max(itemLines.length, polALines.length, polBLines.length, obsLines.length);
          const rowHeight = Math.max(8, maxLines * 4 + 4);
          
          // If row doesn't fit, add new page and re-draw header
          if (yPos + rowHeight > pageHeight - PAGE_MARGIN_BOTTOM) {
            addNewPage();
            drawTableHeader();
            doc.setFontSize(6);
          }
          
          // White background
          doc.setFillColor(255, 255, 255);
          doc.rect(startX, yPos - 3, tableWidth, rowHeight, "F");
          
          if (idx % 2 === 1) {
            doc.setFillColor(245, 245, 245);
            doc.rect(startX, yPos - 3, tableWidth, rowHeight, "F");
          }
          
          // Borders
          doc.setDrawColor(180, 180, 180);
          let xPos = startX;
          colWidths.forEach(w => {
            doc.rect(xPos, yPos - 3, w, rowHeight, "S");
            xPos += w;
          });
          
          // Content
          let cellY = yPos;
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 41, 59);
          itemLines.forEach((line, i) => {
            doc.text(line, startX + 2, cellY + (i * 4));
          });
          
          doc.setFont("helvetica", "normal");
          doc.setTextColor(51, 65, 85);
          polALines.forEach((line, i) => {
            doc.text(line, startX + colWidths[0] + 2, cellY + (i * 4));
          });
          polBLines.forEach((line, i) => {
            doc.text(line, startX + colWidths[0] + colWidths[1] + 2, cellY + (i * 4));
          });
          
          doc.setFont("helvetica", "italic");
          doc.setTextColor(146, 64, 14);
          obsLines.forEach((line, i) => {
            doc.text(line, startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, cellY + (i * 4));
          });
          
          doc.setTextColor(0, 0, 0);
          yPos += rowHeight;
        });
        yPos += 8;
      }

      // Detalle de Coberturas
      if (content.detalle_coberturas?.length > 0) {
        addSectionTitle("DETALLE DE COBERTURAS - LÍMITES, DEDUCIBLES Y PRIMAS", 50);
        
        const dcColWidths = [30, 25, 25, 25, 25, 25, 25];
        const dcTableWidth = dcColWidths.reduce((a, b) => a + b, 0);
        const dcStartX = 15;
        const DC_HEADER_HEIGHT = 14;
        
        const drawDcHeader = () => {
          doc.setFillColor(30, 58, 95);
          doc.rect(dcStartX, yPos - 4, dcColWidths[0], DC_HEADER_HEIGHT, "F");
          doc.setFillColor(37, 99, 235);
          doc.rect(dcStartX + dcColWidths[0], yPos - 4, dcColWidths[1] + dcColWidths[2] + dcColWidths[3], DC_HEADER_HEIGHT / 2, "F");
          doc.setFillColor(22, 163, 74);
          doc.rect(dcStartX + dcColWidths[0] + dcColWidths[1] + dcColWidths[2] + dcColWidths[3], yPos - 4, dcColWidths[4] + dcColWidths[5] + dcColWidths[6], DC_HEADER_HEIGHT / 2, "F");
          
          doc.setFontSize(6);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(255, 255, 255);
          doc.text("COBERTURA", dcStartX + 1, yPos);
          doc.text("PÓLIZA ACTUAL", dcStartX + dcColWidths[0] + 12, yPos);
          doc.text("RENOVACIÓN", dcStartX + dcColWidths[0] + dcColWidths[1] + dcColWidths[2] + dcColWidths[3] + 12, yPos);
          
          const subY = yPos + DC_HEADER_HEIGHT / 2 - 4;
          doc.setFillColor(59, 130, 246);
          const subHeaders = ["Límite", "Deducible", "Prima"];
          let subX = dcStartX + dcColWidths[0];
          for (let i = 0; i < 3; i++) {
            doc.rect(subX, subY, dcColWidths[1 + i], DC_HEADER_HEIGHT / 2, "F");
            doc.text(subHeaders[i], subX + 2, subY + 5);
            subX += dcColWidths[1 + i];
          }
          doc.setFillColor(34, 197, 94);
          for (let i = 0; i < 3; i++) {
            doc.rect(subX, subY, dcColWidths[4 + i], DC_HEADER_HEIGHT / 2, "F");
            doc.text(subHeaders[i], subX + 2, subY + 5);
            subX += dcColWidths[4 + i];
          }
          
          doc.setTextColor(0, 0, 0);
          yPos += DC_HEADER_HEIGHT;
        };
        
        drawDcHeader();
        
        doc.setFontSize(5.5);
        content.detalle_coberturas.forEach((cob, idx) => {
          const cobName = doc.splitTextToSize(cob.cobertura || "", dcColWidths[0] - 3);
          const rowHeight = Math.max(7, cobName.length * 3.5 + 3);
          
          if (yPos + rowHeight > pageHeight - PAGE_MARGIN_BOTTOM) {
            addNewPage();
            drawDcHeader();
            doc.setFontSize(5.5);
          }
          
          if (idx % 2 === 1) {
            doc.setFillColor(248, 250, 252);
            doc.rect(dcStartX, yPos - 3, dcTableWidth, rowHeight, "F");
          }
          
          doc.setDrawColor(220, 220, 220);
          doc.rect(dcStartX, yPos - 3, dcTableWidth, rowHeight, "S");
          
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 41, 59);
          cobName.forEach((line, i) => { doc.text(line, dcStartX + 1, yPos + (i * 3.5)); });
          
          doc.setFont("helvetica", "normal");
          doc.setTextColor(51, 65, 85);
          const cells = [
            cob.poliza_a_limite || "-", cob.poliza_a_deducible || "-", cob.poliza_a_prima || "-",
            cob.poliza_b_limite || "-", cob.poliza_b_deducible || "-", cob.poliza_b_prima || "-"
          ];
          let cellX = dcStartX + dcColWidths[0];
          cells.forEach((val, ci) => {
            const cellLines = doc.splitTextToSize(val, dcColWidths[ci + 1] - 3);
            cellLines.forEach((line, li) => { doc.text(line, cellX + 1, yPos + (li * 3.5)); });
            cellX += dcColWidths[ci + 1];
          });
          
          doc.setTextColor(0, 0, 0);
          yPos += rowHeight;
        });
        yPos += 8;
      }

      // Escenarios Críticos
      if (content.escenarios_criticos?.length > 0) {
        addSectionTitle("ANÁLISIS DE ESCENARIOS CRÍTICOS", 50);
        
        content.escenarios_criticos.forEach((escenario, idx) => {
          // Ensure scenario title + at least some description stay together
          if (getRemainingSpace() < 35) {
            addNewPage();
          }
          
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(`Escenario ${idx + 1}: ${escenario.nombre}`, 15, yPos);
          yPos += 6;
          
          if (escenario.descripcion) {
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            addText(escenario.descripcion, 15, 8);
            yPos += 3;
          }
          
          if (escenario.flujo_reclamacion?.length > 0) {
            doc.setFontSize(7);
            escenario.flujo_reclamacion.forEach(flujo => {
              checkNewPage(14);
              doc.setFont("helvetica", "bold");
              const conceptLines = doc.splitTextToSize(flujo.concepto || "", pageWidth - 25);
              conceptLines.forEach(line => {
                doc.text(line, 20, yPos);
                yPos += 4;
              });
              doc.setFont("helvetica", "normal");
              const flujoText = `A: ${flujo.poliza_a || "N/A"}  |  B: ${flujo.poliza_b || "N/A"}`;
              const flujoLines = doc.splitTextToSize(flujoText, pageWidth - 30);
              flujoLines.forEach(line => {
                checkNewPage(5);
                doc.text(line, 25, yPos);
                yPos += 4;
              });
              yPos += 1;
            });
          }
          
          if (escenario.analisis_impacto) {
            if (getRemainingSpace() < 25) addNewPage();
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text("Análisis de Impacto:", 15, yPos);
            yPos += 5;
            doc.setFont("helvetica", "normal");
            addText(escenario.analisis_impacto, 15, 8);
          }
          yPos += 8;
        });
      }

      // Dictamen Pericial
      if (content.dictamen_pericial) {
        addSectionTitle("DICTAMEN PERICIAL Y RECOMENDACIÓN ESTRATÉGICA", 50);
        
        const dp = content.dictamen_pericial;
        
        if (dp.alerta_fundamental) {
          if (getRemainingSpace() < 25) addNewPage();
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(192, 57, 43);
          doc.text("ALERTA FUNDAMENTAL:", 15, yPos);
          yPos += 5;
          doc.setFont("helvetica", "normal");
          addText(dp.alerta_fundamental, 15, 8);
          doc.setTextColor(0, 0, 0);
          yPos += 3;
        }
        
        if (dp.hipotesis_estrategica) {
          if (getRemainingSpace() < 30) addNewPage();
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.text("Hipótesis Estratégica:", 15, yPos);
          yPos += 5;
          doc.setFont("helvetica", "normal");
          addText(dp.hipotesis_estrategica, 15, 8);
          yPos += 5;
        }
        
        if (dp.calificaciones?.length > 0) {
          if (getRemainingSpace() < 30) addNewPage();
          doc.setFontSize(8);
          dp.calificaciones.forEach(cal => {
            if (getRemainingSpace() < 20) addNewPage();
            doc.setFont("helvetica", "bold");
            doc.text(`${cal.criterio}:`, 15, yPos);
            doc.setFont("helvetica", "normal");
            doc.text(`A: ${cal.poliza_a}  |  B: ${cal.poliza_b}`, 70, yPos);
            yPos += 4;
            if (cal.justificacion) {
              doc.setFont("helvetica", "italic");
              doc.setTextColor(100, 100, 100);
              const justLines = doc.splitTextToSize(cal.justificacion, pageWidth - 35);
              justLines.forEach(line => {
                checkNewPage(5);
                doc.text(line, 20, yPos);
                yPos += 4;
              });
              doc.setTextColor(0, 0, 0);
            }
            yPos += 2;
          });
          yPos += 3;
        }
        
        if (dp.analisis_detallado) {
          if (getRemainingSpace() < 25) addNewPage();
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.text("Análisis Detallado:", 15, yPos);
          yPos += 5;
          doc.setFont("helvetica", "normal");
          addText(dp.analisis_detallado, 15, 8);
          yPos += 5;
        }
        
        if (dp.recomendacion_final) {
          if (getRemainingSpace() < 25) addNewPage();
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 100, 0);
          doc.text("Recomendación Final:", 15, yPos);
          yPos += 5;
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 0);
          addText(dp.recomendacion_final, 15, 8);
          yPos += 5;
        }
      }

      // Comunicación con Aseguradora
      if (content.comunicacion_aseguradora?.cuerpo) {
        addSectionTitle("COMUNICACIÓN CON LA ASEGURADORA", 30);
        
        const ca = content.comunicacion_aseguradora;
        doc.setFontSize(8);
        if (ca.asunto) {
          doc.setFont("helvetica", "bold");
          doc.text(`Asunto: ${ca.asunto}`, 15, yPos);
          yPos += 6;
        }
        doc.setFont("helvetica", "normal");
        addText(ca.cuerpo, 15, 8);
        yPos += 5;
      }

      // Comunicación con Cliente
      if (content.comunicacion_cliente?.cuerpo) {
        addSectionTitle("COMUNICACIÓN CON EL CLIENTE", 30);
        
        const cc = content.comunicacion_cliente;
        doc.setFontSize(8);
        if (cc.saludo) {
          doc.setFont("helvetica", "normal");
          doc.text(cc.saludo, 15, yPos);
          yPos += 6;
        }
        addText(cc.cuerpo, 15, 8);
        
        if (cc.datos_propuesta) {
          yPos += 3;
          if (getRemainingSpace() < 30) addNewPage();
          doc.setFont("helvetica", "bold");
          doc.text("Propuesta económica:", 15, yPos);
          yPos += 5;
          doc.setFont("helvetica", "normal");
          const dprop = cc.datos_propuesta;
          if (dprop.monto_asegurado) { checkNewPage(5); doc.text(`Monto Asegurado: ${dprop.monto_asegurado}`, 20, yPos); yPos += 4; }
          if (dprop.prima_neta) { checkNewPage(5); doc.text(`Prima Neta: ${dprop.prima_neta}`, 20, yPos); yPos += 4; }
          if (dprop.impuestos) { checkNewPage(5); doc.text(`Impuestos: ${dprop.impuestos}`, 20, yPos); yPos += 4; }
          if (dprop.prima_total) { checkNewPage(5); doc.text(`Prima Total: ${dprop.prima_total}`, 20, yPos); yPos += 4; }
        }
        yPos += 5;
      }

      // Nota del Asesor
      if (content.nota_asesor) {
        if (getRemainingSpace() < 25) addNewPage();
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("Nota del Asesor (QAsesores SRL):", 15, yPos);
        yPos += 5;
        doc.setFont("helvetica", "italic");
        addText(content.nota_asesor, 15, 8);
      }
    } else {
      // ============ LEGACY FORMAT ============
      // Documents
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Documentos Comparados:", 20, yPos);
      yPos += 8;
      doc.setFont("helvetica", "normal");
      if (content.documents) {
        doc.text(`• Póliza Actual: ${content.documents.document1 || 'N/A'}`, 25, yPos);
        yPos += 6;
        doc.text(`• Renovación: ${content.documents.document2 || 'N/A'}`, 25, yPos);
        yPos += 15;
      }
      
      // Summary
      if (content.summary) {
        doc.setFont("helvetica", "bold");
        doc.text("Resumen Ejecutivo:", 20, yPos);
        yPos += 8;
        doc.setFont("helvetica", "normal");
        
        const summaryLines = doc.splitTextToSize(content.summary, pageWidth - 40);
        summaryLines.forEach(line => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
        yPos += 10;
      }
      
      // Differences
      if (content.differences && content.differences.length > 0) {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFont("helvetica", "bold");
        doc.text("Diferencias Identificadas:", 20, yPos);
        yPos += 10;
        
        content.differences.forEach((diff, index) => {
          if (yPos > 260) {
            doc.addPage();
            yPos = 20;
          }
          
          doc.setFont("helvetica", "bold");
          doc.text(`${index + 1}. ${diff.aspect || 'Aspecto'}`, 20, yPos);
          yPos += 6;
          
          doc.setFont("helvetica", "normal");
          const val1 = diff.document1 || diff.values?.doc1 || 'N/A';
          const val2 = diff.document2 || diff.values?.doc2 || 'N/A';
          
          const doc1Text = doc.splitTextToSize(`Póliza Actual: ${val1}`, pageWidth - 50);
          doc1Text.forEach(line => {
            doc.text(line, 25, yPos);
            yPos += 5;
          });
          
          const doc2Text = doc.splitTextToSize(`Renovación: ${val2}`, pageWidth - 50);
          doc2Text.forEach(line => {
            doc.text(line, 25, yPos);
            yPos += 5;
          });
          
          yPos += 5;
        });
      }
      
      // Recommendations
      if (content.recommendations) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFont("helvetica", "bold");
        doc.text("Recomendaciones:", 20, yPos);
        yPos += 8;
        doc.setFont("helvetica", "normal");
        
        const recLines = doc.splitTextToSize(content.recommendations, pageWidth - 40);
        recLines.forEach(line => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          doc.text(line, 20, yPos);
          yPos += 6;
        });
      }
    }
    
    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      
      if (content.prepared_by) {
        doc.text(
          `Preparado por: ${content.prepared_by}`,
          pageWidth / 2,
          pageHeight - 18,
          { align: "center" }
        );
      }
      
      doc.text(
        content.footer || "© QAsesores SRL - Corredores de Seguros y Reaseguros",
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
      
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageWidth - 15,
        pageHeight - 10,
        { align: "right" }
      );
    }
    
    doc.save(`analisis-comparativo-${report.id.slice(0, 8)}.pdf`);
    toast.success("PDF descargado");
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="reports-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Informes Ejecutivos
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Todos los informes generados de tus comparaciones
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Multi-selection controls */}
            {isSelectionMode ? (
              <>
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {selectedItems.size} seleccionados
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="select-all-reports-button"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Todos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="deselect-all-reports-button"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Ninguno
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={selectedItems.size === 0 || deletingMultiple}
                  data-testid="delete-selected-reports-button"
                >
                  {deletingMultiple ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Eliminar ({selectedItems.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectionMode}
                  data-testid="cancel-selection-reports-button"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                {reports.length > 0 && isAdmin && (
                  <Button 
                    variant="outline"
                    onClick={toggleSelectionMode}
                    className="text-slate-600 dark:text-slate-300"
                    data-testid="enable-selection-reports-button"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Seleccionar
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Reports List */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white font-['Manrope']">
                  Informes Guardados
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Descarga, visualiza o envía por email los informes
                </CardDescription>
              </div>
              <div className="relative w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, póliza, creador..."
                  className="pl-9 h-9 text-sm"
                  data-testid="search-reports-input"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {searchTerm && (
              <p className="text-xs text-slate-500 mt-1">Mostrando {filteredReports.length} de {reports.length} informes</p>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400 mb-2">No tienes informes generados</p>
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  Los informes se generan desde los resultados de comparación
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredReports.map((report) => (
                  <div
                    key={report.id}
                    className={`flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${
                      isSelectionMode && selectedItems.has(report.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                    data-testid={`report-row-${report.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Selection checkbox */}
                      {isSelectionMode && (
                        <Checkbox
                          checked={selectedItems.has(report.id)}
                          onCheckedChange={() => toggleItemSelection(report.id)}
                          className="w-5 h-5 bg-white dark:bg-slate-700 border-2"
                          data-testid={`checkbox-report-${report.id}`}
                        />
                      )}
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: report.content?.colors?.primary || "#0C314C" }}
                      >
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                          {report.content?.cliente_nombre ? (
                            <span data-testid={`report-cliente-${report.id}`}>{report.content.cliente_nombre}</span>
                          ) : (
                            report.content?.title || "Informe Ejecutivo"
                          )}
                          {report.content?.cliente_nombre && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-[#80BC00]/10 text-[#5a8600] border-[#80BC00]/40 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700">
                              <Building2 className="w-2.5 h-2.5 mr-1" />
                              Cliente
                            </Badge>
                          )}
                          {ramoInformeLabel(report.content?.ramo) && (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${ramoBadgeClass(report.content?.ramo)}`} data-testid={`report-ramo-${report.id}`}>
                              {(report.content?.ramo || "").toLowerCase().includes("incendio")
                                ? <Flame className="w-2.5 h-2.5 mr-1" />
                                : <Car className="w-2.5 h-2.5 mr-1" />}
                              {ramoInformeLabel(report.content?.ramo)}
                            </Badge>
                          )}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {report.content?.documents?.document1} vs {report.content?.documents?.document2}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 dark:text-slate-500 flex-wrap">
                          <Clock className="w-3 h-3" />
                          {formatDate(report.created_at)}
                          {(report.content?.created_by_name || report.content?.prepared_by) && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                              <User className="w-2.5 h-2.5 mr-1" />
                              {report.content?.created_by_name || report.content?.prepared_by}
                            </Badge>
                          )}
                          {report.template_id && (
                            <>
                              <span className="mx-1">•</span>
                              <span className="text-[#45E69A]">Con plantilla</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedReport(report)}
                        className="border-slate-200 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                        data-testid={`view-report-${report.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadReportPDF(report)}
                        className="border-slate-200 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                        data-testid={`download-report-${report.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                      {report.comparison_id && !report.content?.pdf_regenerated_at && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/comparison/${report.comparison_id}?regenerateReportId=${report.id}`)}
                          className="border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/20"
                          data-testid={`regenerate-report-${report.id}`}
                          title="Regenerar PDF con texto corregido (si los textos se ven con espacio entre letras)"
                        >
                          <RotateCw className="w-4 h-4 mr-1" />
                          Regenerar
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEmailDialog(report)}
                        className="border-slate-200 dark:border-slate-600 text-[#0C314C] dark:text-[#45E69A] dark:hover:bg-slate-700"
                        data-testid={`email-report-${report.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                      {canSeeTraza && report.comparison_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openTrazabilidad(report)}
                          className="border-[#193966] text-[#193966] hover:bg-[#193966] hover:text-white dark:border-blue-400 dark:text-blue-300"
                          data-testid={`trazabilidad-report-${report.id}`}
                          title="Ver trazabilidad de auditoría (aval para auditorías)"
                        >
                          <History className="w-4 h-4 mr-1" />
                          Trazabilidad
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trazabilidad Modal (accesible desde Informes Ejecutivos) */}
        <Dialog open={!!trazaReport} onOpenChange={(v) => { if (!v) { setTrazaReport(null); setTrazaComparison(null); } }}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="reports-trazabilidad-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[#193966] dark:text-blue-300">
                <History className="w-5 h-5" /> Trazabilidad del Informe Ejecutivo
              </DialogTitle>
              <DialogDescription>
                {trazaReport?.content?.documents?.document1} vs {trazaReport?.content?.documents?.document2}
                {" — "}Registro de auditoría: cada acción ejecutada desde que se inició la comparación.
              </DialogDescription>
            </DialogHeader>

            {trazaLoading ? (
              <div className="flex flex-col items-center py-12 text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-3" />
                Cargando trazabilidad…
              </div>
            ) : trazaComparison ? (
              <div className="space-y-4" data-testid="reports-trazabilidad-content">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  {trazaComparison.ticket_numero ? (
                    <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2" data-testid="reports-traza-ticket">
                      <Ticket className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">Ticket asociado en Mesa de Ayuda:</span>
                      <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">#{trazaComparison.ticket_numero}</span>
                    </div>
                  ) : <span />}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/comparison/${trazaComparison.id}`)}
                      data-testid="reports-traza-ver-analisis-btn"
                    >
                      <Eye className="w-4 h-4 mr-1.5" /> Ver análisis completo
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => printTrazabilidadPDF(trazaComparison.id)}
                      className="bg-[#193966] hover:bg-[#142c4f] text-white"
                      data-testid="reports-traza-print-btn"
                    >
                      <Printer className="w-4 h-4 mr-1.5" /> Imprimir PDF (Folio + QR)
                    </Button>
                  </div>
                </div>

                {(trazaComparison.trazabilidad || []).length === 0 ? (
                  <p className="text-slate-400 italic py-6 text-center">
                    No hay eventos de trazabilidad registrados para esta comparación.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-[#193966] text-white">
                          <th className="text-left px-4 py-2.5 font-semibold w-28">Fecha</th>
                          <th className="text-left px-4 py-2.5 font-semibold w-24">Hora</th>
                          <th className="text-left px-4 py-2.5 font-semibold w-40">Usuario</th>
                          <th className="text-left px-4 py-2.5 font-semibold w-40">Acción</th>
                          <th className="text-left px-4 py-2.5 font-semibold">Detalle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...(trazaComparison.trazabilidad || [])]
                          .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0))
                          .map((e, i) => {
                            const meta = TRAZA_ACCION_META[e.accion] || { label: e.accion, color: "bg-slate-100 text-slate-600", Icon: FileText };
                            const { fecha, hora } = fmtTrazaFecha(e.timestamp);
                            const Icon = meta.Icon;
                            return (
                              <tr key={i} className={i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800"} data-testid={`reports-trazabilidad-row-${i}`}>
                                <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap">{fecha}</td>
                                <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 whitespace-nowrap font-mono text-xs">{hora}</td>
                                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-200">{e.usuario || "-"}</td>
                                <td className="px-4 py-2.5">
                                  <Badge className={`${meta.color} border-0 gap-1`}>
                                    <Icon className="w-3 h-3" /> {meta.label}
                                  </Badge>
                                </td>
                                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{e.detalle || "-"}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Report Detail Dialog */}
        <Dialog open={!!selectedReport && !showEmailDialog} onOpenChange={() => setSelectedReport(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {selectedReport?.content?.title || "Informe Ejecutivo"}
              </DialogTitle>
              <DialogDescription>
                Generado el {selectedReport && formatDate(selectedReport.created_at)}
              </DialogDescription>
            </DialogHeader>
            
            {selectedReport && (
              <div className="space-y-6 mt-4">
                {/* Logo from Template - 300% bigger */}
                {selectedReport.content?.logo_url && (
                  <div className="flex justify-center py-4">
                    <img 
                      src={selectedReport.content.logo_url} 
                      alt="Logo" 
                      className="h-48 object-contain"
                    />
                  </div>
                )}

                {/* Documents */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Documentos Comparados</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-700">
                        {selectedReport.content?.documents?.document1 || "Póliza Actual"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-700">
                        {selectedReport.content?.documents?.document2 || "Renovación"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {selectedReport.content?.summary && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Resumen Ejecutivo</h4>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {selectedReport.content.summary}
                    </p>
                  </div>
                )}

                {/* Differences */}
                {selectedReport.content?.differences?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Diferencias Clave</h4>
                    <div className="space-y-3">
                      {selectedReport.content.differences.map((diff, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                              <span className="font-medium text-slate-900">{diff.aspect}</span>
                            </div>
                            {diff.importance && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                diff.importance === 'alta' ? 'bg-red-100 text-red-700' :
                                diff.importance === 'media' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {diff.importance.toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500 text-xs mb-1">Póliza Actual</p>
                              <p className="text-slate-700">{diff.document1 || diff.values?.doc1 || "N/A"}</p>
                            </div>
                            <div>
                              <p className="text-slate-500 text-xs mb-1">Renovación</p>
                              <p className="text-slate-700">{diff.document2 || diff.values?.doc2 || "N/A"}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendation */}
                {selectedReport.content?.recommendation && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <h4 className="font-semibold text-green-900">Recomendación de Suscripción</h4>
                    </div>
                    <p className="text-green-800 text-sm">
                      {selectedReport.content.recommendation}
                    </p>
                  </div>
                )}

                {/* Best Practices Recommendations */}
                {selectedReport.content?.recommendations && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600" />
                      <h4 className="font-semibold text-blue-900">Mejores Prácticas de Suscripción</h4>
                    </div>
                    <p className="text-blue-800 text-sm whitespace-pre-wrap">
                      {selectedReport.content.recommendations}
                    </p>
                  </div>
                )}

                {/* Prepared By */}
                {selectedReport.content?.prepared_by && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 pt-2 border-t border-slate-200">
                    <User className="w-4 h-4" />
                    <span>Preparado por: <span className="font-medium text-slate-700">{selectedReport.content.prepared_by}</span></span>
                  </div>
                )}

                {/* Footer from Template */}
                {selectedReport.content?.footer && (
                  <div className="text-center text-xs text-slate-400 pt-4 mt-4 border-t border-slate-100">
                    {selectedReport.content.footer}
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                  <Button
                    variant="outline"
                    onClick={() => openEmailDialog(selectedReport)}
                    className="border-slate-200"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar por Email
                  </Button>
                  <Button
                    onClick={() => downloadReportPDF(selectedReport)}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Descargar PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Email Dialog */}
        <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#0C314C]" />
                Enviar Informe por Email
              </DialogTitle>
              <DialogDescription>
                Envía este informe directamente al email de tu cliente
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="recipient_email">Email del destinatario *</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={emailData.recipient_email}
                  onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})}
                  placeholder="cliente@email.com"
                  data-testid="email-recipient-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="recipient_name">Nombre del destinatario (opcional)</Label>
                <Input
                  id="recipient_name"
                  type="text"
                  value={emailData.recipient_name}
                  onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})}
                  placeholder="Juan Pérez"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="subject">Asunto</Label>
                <Input
                  id="subject"
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  placeholder="Informe de Comparación de Pólizas"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="message">Mensaje personalizado (opcional)</Label>
                <Textarea
                  id="message"
                  value={emailData.message}
                  onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                  placeholder="Adjunto encontrará el informe solicitado..."
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={sendEmail}
                disabled={sendingEmail || !emailData.recipient_email}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="send-email-button"
              >
                {sendingEmail ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendingEmail ? "Enviando..." : "Enviar Email"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-white">
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar {selectedItems.size} informe(s)? 
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirmDialog(false)}
                disabled={deletingMultiple}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSelected}
                disabled={deletingMultiple}
                data-testid="confirm-delete-reports-button"
              >
                {deletingMultiple ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
